import React, {
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  useMemo,
  forwardRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";
import { nanoid } from "nanoid";
import { Close } from "@certa/icons/components/Close";

import { Button, ButtonSizes, ButtonTypes, ButtonVariants } from "../../Button";
import { Dimmer } from "../../Dimmer";

import { CATALYST_Z_INDEXES } from "../../../constants/styles";
import styles from "./ComposableDialog.module.css";

type ComposableDialogContextValue = {
  isOpen: boolean;
  onClose(): void;
  preventCloseOnClickOutside?: boolean;
  canWhiteList?: (element: HTMLElement) => boolean;
  descriptionId?: string;
  setDescriptionId: (id: string | undefined) => void;
};

const ComposableDialogContext =
  createContext<ComposableDialogContextValue | null>(null);

const useComposableDialogContext = () => {
  const context = useContext(ComposableDialogContext);
  if (!context) {
    throw new Error(
      "ComposableDialog compound components must be used within ComposableDialog.Root"
    );
  }
  return context;
};

type ComposableDialogProps = {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preventCloseOnClickOutside?: boolean;
  shouldDisableDialogClose?: boolean;
  canWhiteList?: (element: HTMLElement) => boolean;
  role?: "dialog" | "alertdialog";
  "aria-label": string;
  width?: string | number;
  height?: string | number;
  maxHeight?: string | number;
  minWidth?: string;
  maxWidth?: string;
};

const ComposableDialogRoot = forwardRef<HTMLDivElement, ComposableDialogProps>(
  (
    {
      children,
      open,
      onOpenChange,
      preventCloseOnClickOutside = false,
      shouldDisableDialogClose = false,
      canWhiteList = () => true,
      role = "dialog",
      "aria-label": ariaLabel,
      width = "400px",
      height = "fit-content",
      maxHeight,
      minWidth,
      maxWidth,
      ...props
    },
    ref
  ) => {
    const [descriptionId, setDescriptionId] = useState<string>();
    const modalTriggerElementRef = useRef<HTMLElement | null>();
    const rootElement = document.getElementById("catalyst-dialogs-wrapper");

    const returnFocusToTriggeredElement = useCallback(() => {
      requestAnimationFrame(() => {
        modalTriggerElementRef.current?.focus();
        modalTriggerElementRef.current = null;
      });
    }, []);

    const onClose = useCallback(() => {
      if (!preventCloseOnClickOutside) {
        onOpenChange(false);
      }
    }, [preventCloseOnClickOutside, onOpenChange]);

    useEffect(() => {
      if (open) {
        modalTriggerElementRef.current = document.activeElement as HTMLElement;
      } else {
        returnFocusToTriggeredElement();
      }
    }, [open, returnFocusToTriggeredElement]);

    useLayoutEffect(() => {
      if (rootElement && open) {
        rootElement.setAttribute("role", "presentation");
        rootElement.className = styles.catalystComposableDialogWrapper;
        rootElement.style.zIndex = CATALYST_Z_INDEXES.DIALOG;
      }
    }, [rootElement, open]);

    useEffect(() => {
      const onEscapeKeyHandler = (evt: KeyboardEvent) => {
        if (evt.key === "Escape" && !shouldDisableDialogClose) {
          onClose();
        }
      };

      if (open) {
        window.addEventListener("keydown", onEscapeKeyHandler);
      }

      return () => {
        window.removeEventListener("keydown", onEscapeKeyHandler);
      };
    }, [open, onClose, shouldDisableDialogClose]);

    const contextValue = useMemo(
      () => ({
        isOpen: open,
        onClose,
        preventCloseOnClickOutside,
        canWhiteList,
        descriptionId,
        setDescriptionId
      }),
      [open, onClose, preventCloseOnClickOutside, canWhiteList, descriptionId]
    );

    if (!rootElement || !open) {
      return null;
    }

    return (
      <ComposableDialogContext.Provider value={contextValue}>
        {createPortal(
          <>
            <Dimmer onClick={onClose} scrollLock />
            <FocusLock disabled={!open} whiteList={canWhiteList}>
              <div
                ref={ref}
                role={role}
                aria-modal="true"
                aria-label={ariaLabel}
                aria-describedby={descriptionId}
                className={styles.catalystComposableDialog}
                style={{
                  height,
                  width,
                  maxHeight,
                  minWidth,
                  maxWidth
                }}
                data-testid="catalyst-dialog-content"
                {...props}
              >
                {children}
              </div>
            </FocusLock>
          </>,
          rootElement
        )}
      </ComposableDialogContext.Provider>
    );
  }
);

ComposableDialogRoot.displayName = "ComposableDialogRoot";

type ComposableDialogHeaderProps = {
  children: React.ReactNode;
};

const ComposableDialogHeader = ({ children }: ComposableDialogHeaderProps) => {
  return (
    <div className={styles.catalystComposableDialogHeader}>{children}</div>
  );
};

type ComposableDialogTitleProps = {
  children: React.ReactNode;
  id?: string;
  showCloseIcon?: boolean;
};

const ComposableDialogTitle = ({
  children,
  id,
  showCloseIcon = true
}: ComposableDialogTitleProps) => {
  const { onClose } = useComposableDialogContext();
  const [generatedId] = useState(() => nanoid(6));
  const titleId = id || `catalyst-dialog-title-${generatedId}`;

  return (
    <div id={titleId} className={styles.catalystComposableDialogTitle}>
      <span className={styles.titleText}>{children}</span>
      {showCloseIcon && (
        <Button
          leftIcon={<Close />}
          type={ButtonTypes.ICON}
          variant={ButtonVariants.TEXT}
          size={ButtonSizes.SMALL}
          onClick={onClose}
          aria-label="Close dialog"
        />
      )}
    </div>
  );
};

type ComposableDialogDescriptionProps = {
  children: React.ReactNode;
  id?: string;
};

const ComposableDialogDescription = ({
  children,
  id
}: ComposableDialogDescriptionProps) => {
  const { setDescriptionId } = useComposableDialogContext();
  const [generatedId] = useState(() => nanoid(6));
  const descriptionId = id || `catalyst-dialog-description-${generatedId}`;

  useEffect(() => {
    setDescriptionId(descriptionId);
    return () => setDescriptionId(undefined);
  }, [descriptionId, setDescriptionId]);

  return (
    <div
      id={descriptionId}
      className={styles.catalystComposableDialogDescription}
    >
      {children}
    </div>
  );
};

type ComposableDialogBodyProps = {
  children: React.ReactNode;
};

const ComposableDialogBody = ({ children }: ComposableDialogBodyProps) => {
  return (
    <div className={styles.catalystComposableDialogContent}>{children}</div>
  );
};

type ComposableDialogFooterProps = {
  children: React.ReactNode;
};

const ComposableDialogFooter = ({ children }: ComposableDialogFooterProps) => {
  return (
    <div className={styles.catalystComposableDialogFooter}>{children}</div>
  );
};

export const ComposableDialog = {
  Root: ComposableDialogRoot,
  Header: ComposableDialogHeader,
  Title: ComposableDialogTitle,
  Description: ComposableDialogDescription,
  Body: ComposableDialogBody,
  Footer: ComposableDialogFooter
};
